import { defineBoot } from '#q-app/wrappers'
import { computed } from 'vue'
import ky, { type Options } from 'ky'
import { useAccountStore } from 'src/stores/account'
import { useArea, useCountry } from 'src/router'

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $api: typeof ky
  }
}

const getErrorMessageFromResponseData = (responseData: any) => {
  if (responseData.detail) {
    if (typeof responseData.detail === 'string') {
      return responseData.detail
    } else if (Array.isArray(responseData.detail)) {
      return responseData.detail.map((detail: { msg: string }) => detail.msg).join(' ')
    }
    return null
  }

  if (responseData.error) return responseData.error
  if (responseData.errors) return responseData.errors.join(' ')
  if (Array.isArray(responseData)) return responseData.join(' ')

  return null
}

function createApi(prefixUrl: string) {
  const options: Options = {
    prefix: prefixUrl,
    retry: 0,
    hooks: {
      beforeRequest: [
        ({ request }) => {
          const accessToken = useAccountStore().accessToken
          if (accessToken) {
            request.headers.set('X-Access-Token', `Bearer ${accessToken}`)
          }
        },
      ],
      afterResponse: [
        async ({ request, options, response }) => {
          if (
            [401, 403].includes(response.status) &&
            !options.context?.skipJWTRefresh &&
            !options.context?._sent
          ) {
            await useAccountStore().requestAccessTokenRefresh()
            // @ts-expect-error - ky options type mismatch with exactOptionalPropertyTypes
            return ky(request, {
              ...options,
              context: { ...options.context, _sent: true },
            })
          }

          if (!response.ok) {
            let message = `Request failed with status ${response.status}`
            try {
              const data = await response.clone().json()
              message = getErrorMessageFromResponseData(data) || message
            } catch {
              // Ignore JSON parse errors for non-JSON error responses
            }
            console.error(message)
          }
        },
      ],
    },
  }


  return ky.create(options)
}

export function useApi() {
  const country = useCountry()
  const area = useArea()
  const api = computed(() => {
    if (process.env.CRAFTER_ENV === 'development') {
      return createApi('/api/internal')
    }
    if (process.env.CRAFTER_ENV === 'staging') {
      return createApi('https://stag.yeply.fi/api/internal')
    }
    if (process.env.CRAFTER_ENV === 'production' && country.value) {
      const baseDomain = country.value.baseDomain
      const subDomain = area.value?.subDomain || 'app'
      return createApi(`https://${subDomain}.${baseDomain}/api/internal`)
    }
    return null
  })

  return api
}

export default defineBoot(({ app }) => {
  app.config.globalProperties.$api = ky
})
